import {
    PARTNER_COCKPIT_MAX_NAME_LENGTH,
    PARTNER_COCKPIT_MIN_PASSWORD_LENGTH,
    phoneNumberSchema,
} from '@houzy/schemas/partner-cockpit/user-management';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

import { PasswordField } from '../../../components/PasswordField';
import { onboardingApplicationQueryOptions, useSaveAccountDetailsMutation } from '../api/onboardingApi';
import { OnboardingFooter } from '../components/OnboardingFooter';
import { OnboardingStepContent } from '../components/OnboardingStepContent';
import { OnboardingStepError } from '../components/OnboardingStepError';
import { OnboardingStepHeading } from '../components/OnboardingStepHeading';
import { useOnboardingState, type AccountForm } from '../state/onboardingContext';
import { getNextStep } from '../steps';

// One shape either way, so the error lookup below stays indexable: on a revisit the password is already set and its
// fields are hidden, which is the only case where leaving them empty is allowed.
const accountFormFields = z.object({
    firstName: z.string().trim().min(1, 'ONBOARDING.ACCOUNT.ERRORS.REQUIRED'),
    lastName: z.string().trim().min(1, 'ONBOARDING.ACCOUNT.ERRORS.REQUIRED'),
    email: z.email('ONBOARDING.ACCOUNT.ERRORS.EMAIL'),
    phone: phoneNumberSchema('ONBOARDING.ACCOUNT.ERRORS.PHONE'),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
});

const accountFormSchema = (hasPassword: boolean): ReturnType<typeof accountFormFields.superRefine> =>
    accountFormFields.superRefine((account, ctx) => {
        if (hasPassword) {
            return;
        }
        if ((account.password ?? '').length < PARTNER_COCKPIT_MIN_PASSWORD_LENGTH) {
            ctx.addIssue({
                code: 'custom',
                path: ['password'],
                message: 'ONBOARDING.ACCOUNT.ERRORS.PASSWORD_TOO_SHORT',
            });
        }
        if (!account.confirmPassword) {
            ctx.addIssue({ code: 'custom', path: ['confirmPassword'], message: 'ONBOARDING.ACCOUNT.ERRORS.REQUIRED' });
        } else if (account.password !== account.confirmPassword) {
            ctx.addIssue({
                code: 'custom',
                path: ['confirmPassword'],
                message: 'ONBOARDING.ACCOUNT.PASSWORD_MISMATCH',
            });
        }
    });

export function AccountStep(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { account, setAccount } = useOnboardingState();
    const { data: application } = useQuery(onboardingApplicationQueryOptions);
    const saveAccountDetails = useSaveAccountDetailsMutation();
    const [touched, setTouched] = useState<Partial<Record<keyof AccountForm, boolean>>>({});

    const update = <K extends keyof AccountForm>(key: K, value: AccountForm[K]): void => {
        setAccount((prev) => ({ ...prev, [key]: value }));
    };

    const verifiedEmail = application?.email;
    useEffect(() => {
        if (verifiedEmail && account.email !== verifiedEmail) {
            setAccount((prev) => ({ ...prev, email: verifiedEmail }));
        }
    }, [verifiedEmail, account.email, setAccount]);

    const prefilled = useRef(false);
    const savedAccount = application?.accountDetails;
    useEffect(() => {
        if (prefilled.current || !savedAccount) {
            return;
        }
        prefilled.current = true;
        setAccount((prev) => {
            if (prev.firstName || prev.lastName || prev.phone) {
                return prev;
            }
            return {
                ...prev,
                firstName: savedAccount.firstName ?? '',
                lastName: savedAccount.lastName ?? '',
                phone: savedAccount.phoneNumber ?? '',
            };
        });
    }, [savedAccount, setAccount]);

    const markTouched = (key: keyof AccountForm): void => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const hasPassword = application?.accountDetails?.hasPassword === true;
    const parsed = accountFormSchema(hasPassword).safeParse(account);
    const fieldErrors = parsed.success ? undefined : z.flattenError(parsed.error).fieldErrors;

    const errorFor = (key: keyof AccountForm): string | undefined => {
        if (!touched[key]) {
            return undefined;
        }
        const messageKey = fieldErrors?.[key]?.[0];
        return messageKey ? t(messageKey) : undefined;
    };

    const handleNext = (): void => {
        if (!parsed.success) {
            return;
        }
        saveAccountDetails.mutate(
            {
                firstName: parsed.data.firstName,
                lastName: parsed.data.lastName,
                phoneNumber: parsed.data.phone,
                ...(hasPassword || parsed.data.password === undefined ? {} : { password: parsed.data.password }),
            },
            {
                onSuccess: () => {
                    const next = getNextStep('account');
                    if (next) {
                        void navigate({ to: next.path });
                    }
                },
            },
        );
    };

    return (
        <>
            <OnboardingStepContent>
                <Stack spacing={8.5}>
                    <OnboardingStepHeading
                        title={t('ONBOARDING.ACCOUNT.TITLE')}
                        description={t('ONBOARDING.ACCOUNT.DESCRIPTION')}
                    />

                    <OnboardingStepError error={saveAccountDetails.error} />

                    <Stack component="form" spacing={2} noValidate>
                        <TextField
                            label={t('ONBOARDING.ACCOUNT.FIELDS.FIRST_NAME')}
                            fullWidth
                            autoComplete="given-name"
                            slotProps={{ htmlInput: { maxLength: PARTNER_COCKPIT_MAX_NAME_LENGTH } }}
                            value={account.firstName}
                            onChange={(e) => update('firstName', e.target.value)}
                            onBlur={() => markTouched('firstName')}
                            error={Boolean(errorFor('firstName'))}
                            helperText={errorFor('firstName')}
                        />
                        <TextField
                            label={t('ONBOARDING.ACCOUNT.FIELDS.LAST_NAME')}
                            fullWidth
                            autoComplete="family-name"
                            slotProps={{ htmlInput: { maxLength: PARTNER_COCKPIT_MAX_NAME_LENGTH } }}
                            value={account.lastName}
                            onChange={(e) => update('lastName', e.target.value)}
                            onBlur={() => markTouched('lastName')}
                            error={Boolean(errorFor('lastName'))}
                            helperText={errorFor('lastName')}
                        />
                        {/* Email is fixed to the verified onboarding address and cannot be changed here. */}
                        <TextField
                            label={t('ONBOARDING.ACCOUNT.FIELDS.EMAIL')}
                            type="email"
                            fullWidth
                            autoComplete="email"
                            value={account.email}
                            slotProps={{ input: { readOnly: true } }}
                            helperText={t('ONBOARDING.ACCOUNT.EMAIL_LOCKED_HINT')}
                        />
                        <TextField
                            label={t('ONBOARDING.ACCOUNT.FIELDS.PHONE')}
                            type="tel"
                            fullWidth
                            autoComplete="tel"
                            value={account.phone}
                            onChange={(e) => update('phone', e.target.value)}
                            onBlur={() => markTouched('phone')}
                            error={Boolean(errorFor('phone'))}
                            helperText={errorFor('phone')}
                        />
                        {hasPassword ? (
                            <Typography variant="body2" color="text.secondary">
                                {t('ONBOARDING.ACCOUNT.PASSWORD_ALREADY_SET')}
                            </Typography>
                        ) : (
                            <PasswordField
                                label={t('ONBOARDING.ACCOUNT.FIELDS.PASSWORD')}
                                fullWidth
                                autoComplete="new-password"
                                value={account.password}
                                onChange={(e) => update('password', e.target.value)}
                                onBlur={() => markTouched('password')}
                                error={Boolean(errorFor('password'))}
                                helperText={errorFor('password')}
                            />
                        )}
                        {!hasPassword && (
                            <PasswordField
                                label={t('ONBOARDING.ACCOUNT.FIELDS.CONFIRM_PASSWORD')}
                                fullWidth
                                autoComplete="new-password"
                                value={account.confirmPassword}
                                onChange={(e) => update('confirmPassword', e.target.value)}
                                onBlur={() => markTouched('confirmPassword')}
                                error={Boolean(errorFor('confirmPassword'))}
                                helperText={errorFor('confirmPassword')}
                            />
                        )}
                    </Stack>
                </Stack>
            </OnboardingStepContent>

            <OnboardingFooter
                currentStep="account"
                nextDisabled={!parsed.success || saveAccountDetails.isPending}
                onNext={handleNext}
            />
        </>
    );
}
